import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { weddingEssentialsAPI } from '../../api/weddingEssentialsAPI';
import { analyticsAPI } from '../../api/itemEngagementAPI';
import { trackItemClicked } from '../../utils/analytics';
import { formatItemPriceEgp } from '../../utils/formatPrice';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FeaturedStrip from '../../components/items/FeaturedStrip';
import VerticalPageSeo from '../../components/seo/VerticalPageSeo';
import CategoryCarousel from '../../components/items/CategoryCarousel';
import {
  WEDDING_DEPARTMENTS,
  getWeddingCategoriesForDepartment,
  getWeddingCategoryDepartment,
  getWeddingPreLovedAttributes,
} from '../../constants/weddingCategories';
import BrowseFiltersToolbar, { buildBrowseFetchParams } from '../../components/items/BrowseFiltersToolbar';
import { EMPTY_BROWSE_FILTERS } from '../../constants/itemBrowseFilters';
import { getApproximateViewerCoordinates } from '../../utils/viewerGeo';
import { getListingDetailPath, getListingLinkState } from '../../utils/itemLinks';
import { sizedImageUrl } from '../../utils/listingImages';
import ItemFavoriteButton from '../../components/items/ItemFavoriteButton';
import { resolveListingBrand } from '../../components/items/ListingDetailShared';
import { listingKindToFavoriteKind } from '../../utils/favoriteKeys';
import WeddingEssentialsPolicy from '../../components/items/WeddingEssentialsPolicy';
import { WEDDING_DRESS_OPTIONS } from '../../constants/weddingDressSchema';

const WeddingPageShell = styled.div`
  min-height: calc(100vh - 120px);
  background:
    radial-gradient(ellipse 90% 55% at 50% -10%, rgba(244, 114, 182, 0.14), transparent 55%),
    linear-gradient(180deg, #f4e9ef 0%, #eadde6 48%, #e2d3dc 100%);
`;

const WeddingContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0.5rem 1rem 3rem;
`;

const ItemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.5rem;
`;

const ItemCard = styled(Link)`
  display: block;
  text-decoration: none;
  color: inherit;
  cursor: pointer;
  background: transparent;
  overflow: hidden;
  border: none;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 0.92;
  }
`;

const ItemImage = styled.div`
  width: 100%;
  aspect-ratio: 1;
  background: var(--bg-secondary);
  position: relative;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
    transform: scale(1.1);
  }
`;
const ItemContent = styled.div`
  padding: 0.55rem 0.15rem 0.25rem;
`;

const ItemPrice = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: 0.2rem;
`;

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.75rem;
  color: var(--text-light);
  min-width: 0;
`;

const MetaBrand = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`;

const HeroActions = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const ListPreLovedButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.75rem;
  background: #fff;
  color: #9d174d;
  font-weight: 700;
  font-size: 1rem;
  border: 2px solid #be185d;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: transform 0.15s, background 0.15s;
  &:hover {
    transform: translateY(-1px);
    background: #fdf2f8;
  }
`;

const NoItemsMessage = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: var(--text-secondary);
`;

const LoadMoreSentinel = styled.div`
  height: 1px;
  width: 100%;
`;

const DepartmentBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
  margin: 0 0 0.75rem;
  padding: 0.65rem 0.85rem;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 0.625rem;
`;

const DepartmentLabel = styled.span`
  font-size: 0.9rem;
  font-weight: 600;
  color: #831843;
`;

const DepartmentChip = styled.button`
  padding: 0.45rem 0.9rem;
  border-radius: 999px;
  border: 1.5px solid #f9a8d4;
  background: ${(p) => (p.$active ? '#be185d' : '#fff')};
  color: ${(p) => (p.$active ? '#fff' : '#9d174d')};
  font-size: 0.85rem;
  font-weight: 700;
  cursor: pointer;
`;

const WeddingFiltersPanel = styled.section`
  margin: 1rem 0 1.25rem;
  padding: 1rem 1.1rem 0.75rem;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #f9a8d4;
  border-radius: 1rem;
  box-shadow: 0 10px 30px rgba(131, 24, 67, 0.08);
`;

const WeddingFiltersHeading = styled.div`
  margin-bottom: 0.45rem;

  h2 {
    margin: 0;
    color: #831843;
    font-size: 1.05rem;
  }

  p {
    margin: 0.2rem 0 0;
    color: #9d174d;
    font-size: 0.82rem;
  }
`;

const EmptyImage = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
`;

const IMAGE_BASE =
  typeof window !== 'undefined' &&
  (window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1')
    ? 'http://127.0.0.1:8080'
    : '';

function buildWeddingBrowseCategories(department = '') {
  const scoped = getWeddingCategoriesForDepartment(department);
  return [
    { name: 'All', icon: '💒', value: '' },
    ...scoped.map((cat) => ({
      name: cat.name,
      icon: cat.icon,
      value: cat.apiCategory,
    })),
  ];
}

function listingDetailPath(item) {
  return getListingDetailPath(item, item.listingKind);
}

function listingLinkState(item) {
  return getListingLinkState(item, item.listingKind);
}

const WeddingEssentialsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isAuthenticated } = useSelector((s) => s.auth);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [browseFilters, setBrowseFilters] = useState({
    ...EMPTY_BROWSE_FILTERS,
    category: '',
  });
  const [sort, setSort] = useState('newest');
  const weddingBrowseCategories = useMemo(
    () => buildWeddingBrowseCategories(browseFilters.department),
    [browseFilters.department],
  );
  const showDressStyleFilters =
    browseFilters.department !== 'Men'
    && getWeddingCategoryDepartment(browseFilters.category) !== 'Men';
  const weddingFilterOptions = useMemo(() => {
    const categories = browseFilters.category
      ? [browseFilters.category]
      : getWeddingCategoriesForDepartment(browseFilters.department).map((cat) => cat.apiCategory);
    const attributes = categories
      .map((category) => getWeddingPreLovedAttributes(category))
      .filter(Boolean);
    return {
      colors: [...new Set(attributes.flatMap((entry) => entry.colors || []))],
      sizes: [...new Set(attributes.flatMap(
        (entry) => entry.sizeProfile?.groups?.flatMap((group) => group.sizes) || [],
      ))],
    };
  }, [browseFilters.category, browseFilters.department]);
  const [viewerCoords, setViewerCoords] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const loadMoreRef = useRef(null);
  const loadMoreObserverRef = useRef(null);
  const impSent = useRef(new Set());
  const observerRef = useRef(null);

  const cardRef = useCallback((node) => {
    if (!node) return;
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const id = Number(entry.target.dataset.listingId);
            const kind = entry.target.dataset.listingKind;
            const key = `${kind}-${id}`;
            if (id && kind && !impSent.current.has(key)) {
              impSent.current.add(key);
              analyticsAPI.recordImpression(id, kind);
            }
          });
        },
        { threshold: 0.5 },
      );
    }
    observerRef.current.observe(node);
  }, []);

  useEffect(() => {
    return () => {
      analyticsAPI.flushImpressions();
      observerRef.current?.disconnect();
    };
  }, []);

  useEffect(() => {
    getApproximateViewerCoordinates().then((c) => {
      if (c) setViewerCoords(c);
    });
  }, []);

  const nearestCoords = sort === 'nearest' ? viewerCoords : null;
  const fetchPage = useCallback(
    async (targetPage, replace = false) => {
      if (replace) setLoading(true);
      else setLoadingMore(true);
      try {
        const geoExtra = nearestCoords
          ? { user_lat: nearestCoords.lat, user_lon: nearestCoords.lon }
          : {};
        const payload = await weddingEssentialsAPI.getWeddingListingsPage(
          buildBrowseFetchParams(browseFilters, sort, geoExtra),
          targetPage,
          24,
          sort,
        );
        const nextResults = Array.isArray(payload?.results) ? payload.results : [];
        setItems((prev) => (replace ? nextResults : [...prev, ...nextResults]));
        setPage(payload?.page || targetPage);
        setHasNext(Boolean(payload?.has_next));
        setTotalCount(payload?.count ?? nextResults.length);
      } catch (err) {
        console.error('WeddingEssentialsPage: fetch failed', err);
        if (replace) {
          setItems([]);
          setHasNext(false);
          setTotalCount(0);
        }
      } finally {
        if (replace) setLoading(false);
        else setLoadingMore(false);
      }
    },
    [browseFilters, sort, nearestCoords],
  );

  useEffect(() => {
    impSent.current.clear();
    fetchPage(1, true);
  }, [fetchPage]);

  useEffect(() => {
    if (!loadMoreRef.current) return undefined;
    if (loadMoreObserverRef.current) loadMoreObserverRef.current.disconnect();
    loadMoreObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasNext && !loadingMore && !loading) {
          fetchPage(page + 1, false);
        }
      },
      { rootMargin: '300px', threshold: 0.01 },
    );
    loadMoreObserverRef.current.observe(loadMoreRef.current);
    return () => loadMoreObserverRef.current?.disconnect();
  }, [hasNext, loadingMore, loading, page, fetchPage]);

  const getImageUrl = (item) => {
    if (item.image_url) {
      return item.image_url.startsWith('http') ? item.image_url : `${IMAGE_BASE}${item.image_url}`;
    }
    if (item.images?.length) {
      const img = item.images[0].image ?? item.images[0];
      return typeof img === 'string' && img.startsWith('http') ? img : `${IMAGE_BASE}${img}`;
    }
    return null;
  };

  const handleDepartmentSelect = (department) => {
    setBrowseFilters((prev) => {
      const nextDepartment = prev.department === department ? '' : department;
      const categoryDepartment = getWeddingCategoryDepartment(prev.category);
      const categoryStillValid =
        !prev.category ||
        !nextDepartment ||
        categoryDepartment === nextDepartment ||
        categoryDepartment === null;
      return {
        ...prev,
        department: nextDepartment,
        category: categoryStillValid ? prev.category : '',
        subcategory: categoryStillValid ? prev.subcategory : '',
        book_subcategory: categoryStillValid ? prev.book_subcategory : '',
        size: categoryStillValid ? prev.size : '',
        silhouette: nextDepartment === 'Men' ? '' : prev.silhouette,
        vibe: nextDepartment === 'Men' ? '' : prev.vibe,
        train: nextDepartment === 'Men' ? '' : prev.train,
        is_hijabi: nextDepartment === 'Men' ? '' : prev.is_hijabi,
      };
    });
  };

  if (loading && items.length === 0) {
    return (
      <WeddingPageShell>
        <LoadingSpinner text="Loading wedding essentials…" />
      </WeddingPageShell>
    );
  }

  return (
    <WeddingPageShell>
    <WeddingContainer>
      <VerticalPageSeo showIntro={false} />

      <CategoryCarousel
        categories={weddingBrowseCategories}
        selectedCategory={browseFilters.category || ''}
        onSelectCategory={(cat) =>
          setBrowseFilters((prev) => ({
            ...prev,
            category: cat,
            subcategory: '',
            book_subcategory: '',
            size: '',
            silhouette: getWeddingCategoryDepartment(cat) === 'Men' ? '' : prev.silhouette,
            vibe: getWeddingCategoryDepartment(cat) === 'Men' ? '' : prev.vibe,
            train: getWeddingCategoryDepartment(cat) === 'Men' ? '' : prev.train,
            is_hijabi: getWeddingCategoryDepartment(cat) === 'Men' ? '' : prev.is_hijabi,
          }))
        }
        variant="pink"
      />

      <DepartmentBar>
        <DepartmentLabel>{t('nav.shopByDepartment')}</DepartmentLabel>
        {WEDDING_DEPARTMENTS.map((department) => (
          <DepartmentChip
            key={department}
            type="button"
            $active={browseFilters.department === department}
            onClick={() => handleDepartmentSelect(department)}
          >
            {department === 'Men' ? t('nav.departmentMen') : t('nav.departmentWomen')}
          </DepartmentChip>
        ))}
      </DepartmentBar>

      <HeroActions>
        <ListPreLovedButton
          onClick={() =>
            navigate(isAuthenticated ? '/create-item?group=wedding' : '/login')
          }
        >
          👗 List Bridal Gown / Soiree Dress
        </ListPreLovedButton>
      </HeroActions>

      <WeddingEssentialsPolicy />

      <FeaturedStrip
        scope="wedding_items"
        department={browseFilters.department || ''}
      />

      <WeddingFiltersPanel>
        <WeddingFiltersHeading>
          <h2>Find your perfect wedding look</h2>
          <p>Filter by category, size, color, weight, Hijabi style, and price.</p>
        </WeddingFiltersHeading>
        <BrowseFiltersToolbar
          filters={browseFilters}
          onFiltersChange={setBrowseFilters}
          categories={weddingBrowseCategories}
          sort={sort}
          onSortChange={setSort}
          hideDepartmentFilter
          hideConditionFilter
          hideSecondaryFilters
          showHijabiFilter={showDressStyleFilters}
          sizeOptions={weddingFilterOptions.sizes}
          colorOptions={weddingFilterOptions.colors}
          silhouetteOptions={showDressStyleFilters ? WEDDING_DRESS_OPTIONS.silhouette : []}
          vibeOptions={showDressStyleFilters ? WEDDING_DRESS_OPTIONS.vibe : []}
          trainOptions={showDressStyleFilters ? WEDDING_DRESS_OPTIONS.train : []}
          weightOptions={WEDDING_DRESS_OPTIONS.weight_class}
        />
      </WeddingFiltersPanel>

      {items.length === 0 ? (
        <NoItemsMessage>
          <h3>No listings yet</h3>
          <p>List bridal gowns, soiree dresses, groom wear, or wedding accessories with “List Bridal Gown / Soiree Dress” above.</p>
        </NoItemsMessage>
      ) : (
        <>
          <ItemsGrid>
            {items.map((item) => {
              const imageUrl = getImageUrl(item);
              return (
                <ItemCard
                  key={item.key}
                  to={listingDetailPath(item)}
                  state={listingLinkState(item)}
                  onClick={() => {
                    trackItemClicked(item.id, {
                      item_type: item.listingKind,
                      category: item.category,
                    });
                    analyticsAPI.recordClick(item.id, item.listingKind);
                  }}
                  ref={cardRef}
                  data-listing-id={item.id}
                  data-listing-kind={item.listingKind}
                >
                  <ItemImage>
                    <ItemFavoriteButton
                      itemId={item.id}
                      favoriteKind={listingKindToFavoriteKind(item.listingKind)}
                      category={item.category}
                    />
                    {imageUrl ? (
                      <img
                        src={sizedImageUrl(imageUrl, 360) || imageUrl}
                        alt={item.title}
                        loading="lazy"
                        decoding="async"
                        style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center', transform: 'scale(1.1)' }}
                      />
                    ) : (
                      <EmptyImage>💒</EmptyImage>
                    )}
                  </ItemImage>
                  <ItemContent>
                    <ItemPrice>{formatItemPriceEgp(item.price)}</ItemPrice>
                    <ItemMeta>
                      <MetaBrand>{resolveListingBrand(item) || '—'}</MetaBrand>
                      {item.condition ? <span>· {item.condition}</span> : null}
                    </ItemMeta>
                  </ItemContent>
                </ItemCard>
              );
            })}
          </ItemsGrid>
          {loadingMore && <LoadingSpinner text="Loading more…" />}
          {!hasNext && items.length > 0 && (
            <NoItemsMessage>
              <p>
                Showing {items.length}
                {totalCount > items.length ? ` of ${totalCount}` : ''} listings.
              </p>
            </NoItemsMessage>
          )}
          <LoadMoreSentinel ref={loadMoreRef} />
        </>
      )}
    </WeddingContainer>
    </WeddingPageShell>
  );
};

export default WeddingEssentialsPage;
